import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ChevronLeft, Calendar, Pill, Stethoscope, Flame, Filter, ZoomIn, ZoomOut, ChevronDown, LineChart, List, TrendingDown, Bug, Activity, Moon, Brain, Syringe, TrendingUp, Lightbulb, Search, Cloud, Heart, TestTube, GraduationCap, Paperclip } from 'lucide-react';
import { LineChart as RechartsLineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Area, ComposedChart, Legend, Scatter } from 'recharts';
import InsightsPage from './InsightsPage';
import CorrelationInsights from './CorrelationInsights';
import SmartCorrelations from './SmartCorrelations';
import { CorrelationAnalyzer } from './CorrelationAnalyzer';
import { formatLocalDate } from '../utils/dateUtils';
const API_URL = process.env.REACT_APP_BACKEND_URL;

const MedicalTimeline = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [filters, setFilters] = useState({ medications: true, supplements: true, appointments: true, flares: true, vaccines: true, symptoms: true, sleep: true, weather: true, cycle: true, labs: true, teacher: true });
  const [timeRange, setTimeRange] = useState('6m'); // 3m, 6m, 1y, all
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [viewMode, setViewMode] = useState('timeline'); // 'timeline', 'graph', 'metrics', 'insights', 'correlations'
  const [rawFlares, setRawFlares] = useState([]);
  const [rawSupplements, setRawSupplements] = useState([]);
  const [rawAppointments, setRawAppointments] = useState([]);
  const [rawSymptoms, setRawSymptoms] = useState([]);
  const [rawSleepLogs, setRawSleepLogs] = useState([]);
  const [rawVaccines, setRawVaccines] = useState([]);
  const [rawWeather, setRawWeather] = useState([]);
  const [rawCycle, setRawCycle] = useState([]);
  const [rawLabs, setRawLabs] = useState([]);
  const [rawTeacherReports, setRawTeacherReports] = useState([]);

  useEffect(() => {
    fetchAllData();
  }, [token]);

  useEffect(() => {
    applyFilters();
  }, [events, filters, timeRange]);

  const fetchAllData = async () => {
    try {
      const [supplementsRes, appointmentsRes, flaresRes, symptomsRes, sleepRes, vaccinesRes, weatherRes, cycleRes, labsRes, teacherRes] = await Promise.all([
        fetch(`${API_URL}/api/supplements`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/api/appointments`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/api/flare-journal`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/api/symptoms`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/api/sleep`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/api/vaccines`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/api/weather?days=365`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/api/cycle`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/api/labs`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/api/teacher/all-reports?days=365`, { headers: { Authorization: `Bearer ${token}` } })
      ]);

      const supplementsData = await supplementsRes.json();
      const appointmentsData = await appointmentsRes.json();
      const flaresData = await flaresRes.json();
      const symptomsData = symptomsRes.ok ? await symptomsRes.json() : [];
      const sleepData = sleepRes.ok ? await sleepRes.json() : { entries: [] };
      const vaccinesData = vaccinesRes.ok ? await vaccinesRes.json() : [];
      const weatherData = weatherRes.ok ? await weatherRes.json() : [];
      const cycleData = cycleRes.ok ? await cycleRes.json() : { entries: [] };
      const labsData = labsRes.ok ? await labsRes.json() : { results: [] };
      const teacherData = teacherRes.ok ? await teacherRes.json() : { reports: [] };

      const supplements = Array.isArray(supplementsData?.supplements) ? supplementsData.supplements : [];
      const appointments = Array.isArray(appointmentsData?.appointments) ? appointmentsData.appointments : [];
      // Flare journal returns array directly or object with entries
      const flares = Array.isArray(flaresData) ? flaresData : (Array.isArray(flaresData?.entries) ? flaresData.entries : []);
      const symptoms = Array.isArray(symptomsData) ? symptomsData : [];
      const sleepLogsRaw = sleepData?.entries || sleepData?.logs || sleepData;
      const sleepLogs = Array.isArray(sleepLogsRaw) ? sleepLogsRaw : [];
      const vaccines = Array.isArray(vaccinesData) ? vaccinesData : [];
      const weatherLogs = Array.isArray(weatherData) ? weatherData : (Array.isArray(weatherData?.entries) ? weatherData.entries : []);
      const cycleLogs = Array.isArray(cycleData?.entries) ? cycleData.entries : (Array.isArray(cycleData) ? cycleData : []);
      const labs = Array.isArray(labsData?.results) ? labsData.results : (Array.isArray(labsData) ? labsData : []);
      const teacherReports = Array.isArray(teacherData?.reports) ? teacherData.reports : [];
      
      // Fetch attachment counts for all parent types (one bulk call per type)
      const attachmentCountsByType = {
        appointment: {},
        symptom: {},
        flare: {},
        lab: {},
        vaccine: {},
      };
      const buildAttachCounts = async (parentType, items) => {
        if (!items || items.length === 0) return;
        try {
          const ids = items.map(i => i.id).filter(Boolean).join(',');
          if (!ids) return;
          const r = await fetch(
            `${API_URL}/api/attachments/by-records?parent_type=${parentType}&parent_ids=${encodeURIComponent(ids)}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (r.ok) {
            const data = await r.json();
            (data.attachments || []).forEach(a => {
              attachmentCountsByType[parentType][a.parent_id] = (attachmentCountsByType[parentType][a.parent_id] || 0) + 1;
            });
          }
        } catch (e) { /* noop */ }
      };
      await Promise.all([
        buildAttachCounts('appointment', appointments),
        buildAttachCounts('symptom', symptoms),
        buildAttachCounts('flare', flares),
        buildAttachCounts('lab', labs),
        buildAttachCounts('vaccine', vaccines),
      ]);
      const attachmentCounts = attachmentCountsByType.appointment;
      
      // Store raw data for graph view
      setRawFlares(flares);
      setRawSupplements(supplements);
      setRawAppointments(appointments);
      setRawSymptoms(symptoms);
      setRawSleepLogs(Array.isArray(sleepLogs) ? sleepLogs : []);
      setRawVaccines(vaccines);
      setRawWeather(weatherLogs);
      setRawCycle(cycleLogs);
      setRawLabs(labs);
      setRawTeacherReports(teacherReports);

      const allEvents = [];

      // Process supplements/medications
      supplements.forEach(s => {
        allEvents.push({
          id: `${s.id}-start`,
          date: s.start_date,
          type: s.type === 'medication' ? 'medication_start' : 'supplement_start',
          title: `Started ${s.name}`,
          subtitle: `${s.dosage} - ${s.frequency}`,
          details: s,
          color: s.type === 'medication' ? 'purple' : 'green'
        });
        if (s.end_date || s.status === 'discontinued') {
          allEvents.push({
            id: `${s.id}-end`,
            date: s.end_date || s.start_date,
            type: s.type === 'medication' ? 'medication_stop' : 'supplement_stop',
            title: `Stopped ${s.name}`,
            subtitle: s.status === 'discontinued' ? 'Discontinued' : 'Completed',
            details: s,
            color: 'red'
          });
        }
      });

      // Process appointments
      appointments.forEach(a => {
        allEvents.push({
          id: `appt-${a.id}`,
          date: a.date,
          type: 'appointment',
          title: a.provider_name,
          subtitle: a.appointment_type || 'Appointment',
          details: a,
          color: 'blue',
          attachmentCount: attachmentCounts[a.id] || 0
        });
      });

      // Process flares
      flares.forEach(f => {
        allEvents.push({
          id: `flare-${f.id}`,
          date: f.date,
          type: 'flare',
          title: `Flare Day (Severity: ${f.severity}/10)`,
          subtitle: f.symptoms?.slice(0, 3).join(', ') || 'Symptoms logged',
          details: f,
          color: 'orange',
          attachmentCount: attachmentCountsByType.flare[f.id] || 0
        });
      });

      // Process vaccines
      vaccines.forEach(v => {
        const hasReaction = v.reactions && v.reactions.length > 0;
        allEvents.push({
          id: `vaccine-${v.id}`,
          date: v.date_administered,
          type: 'vaccine',
          title: v.vaccine_name,
          subtitle: hasReaction ? `⚠️ Reaction reported` : v.vaccine_type.replace('_', ' '),
          details: v,
          color: hasReaction ? 'red' : 'teal',
          attachmentCount: attachmentCountsByType.vaccine[v.id] || 0
        });
      });

      // Process symptom entries
      symptoms.forEach(s => {
        if (s.date) {
          const severityLabel = s.severity <= 3 ? 'Mild' : s.severity <= 6 ? 'Moderate' : 'Severe';
          allEvents.push({
            id: `symptom-${s.id || s._id}`,
            date: s.date,
            type: 'symptom',
            title: `Symptom Entry (${severityLabel})`,
            subtitle: s.mood_status ? `Mood: ${s.mood_status}` : `Severity: ${s.severity}/10`,
            details: s,
            color: s.severity <= 3 ? 'green' : s.severity <= 6 ? 'yellow' : 'orange',
            attachmentCount: attachmentCountsByType.symptom[s.id || s._id] || 0
          });
        }
      });

      // Process sleep logs
      sleepLogs.forEach(s => {
        if (s.date) {
          const qualityLabel = s.quality >= 4 ? 'Good' : s.quality >= 2 ? 'Fair' : 'Poor';
          allEvents.push({
            id: `sleep-${s.id || s._id}`,
            date: s.date,
            type: 'sleep',
            title: `Sleep: ${s.hours_slept || s.hours}h (${qualityLabel})`,
            subtitle: s.issues?.length > 0 ? s.issues.join(', ') : 'No issues reported',
            details: s,
            color: s.quality >= 4 ? 'indigo' : s.quality >= 2 ? 'yellow' : 'red'
          });
        }
      });

      // Process weather logs (only significant ones)
      weatherLogs.forEach(w => {
        if (w.date && w.pressure_change && Math.abs(w.pressure_change) > 5) {
          allEvents.push({
            id: `weather-${w.id || w._id}`,
            date: w.date,
            type: 'weather',
            title: `Weather: ${w.conditions || 'Recorded'}`,
            subtitle: `Pressure change: ${w.pressure_change > 0 ? '+' : ''}${w.pressure_change?.toFixed(1)} hPa`,
            details: w,
            color: 'sky'
          });
        }
      });

      // Process cycle logs
      cycleLogs.forEach(c => {
        if (c.date) {
          allEvents.push({
            id: `cycle-${c.id || c._id}`,
            date: c.date,
            type: 'cycle',
            title: c.phase ? `Cycle: ${c.phase}` : 'Cycle Entry',
            subtitle: c.symptoms?.length > 0 ? c.symptoms.slice(0, 2).join(', ') : c.flow || 'Logged',
            details: c,
            color: 'pink'
          });
        }
      });

      // Process lab results
      labs.forEach(l => {
        if (l.date || l.result_date) {
          const labDate = l.date || l.result_date;
          const hasAbnormal = l.results?.some(r => r.flag === 'abnormal' || r.flag === 'high' || r.flag === 'low');
          allEvents.push({
            id: `lab-${l.id || l._id}`,
            date: labDate,
            type: 'lab',
            title: l.test_type || l.lab_name || 'Lab Results',
            subtitle: hasAbnormal ? '⚠️ Abnormal results' : `${l.results?.length || 0} values recorded`,
            details: l,
            color: hasAbnormal ? 'red' : 'cyan',
            attachmentCount: attachmentCountsByType.lab[l.id || l._id] || 0
          });
        }
      });

      // Process teacher reports
      const ratingLabels = {
        much_better: 'Much better',
        slightly_better: 'Slightly better',
        typical: 'Typical day',
        slightly_worse: 'Slightly worse',
        much_worse: 'Much worse',
      };
      teacherReports.forEach(r => {
        if (!r.report_date) return;
        const sev = r.severity_score || 5;
        const color = sev >= 7 ? 'red' : sev >= 5 ? 'orange' : sev >= 4 ? 'yellow' : 'emerald';
        allEvents.push({
          id: `teacher-${r.id}`,
          date: r.report_date,
          type: 'teacher',
          title: `School Check-In: ${r.child_name} (${ratingLabels[r.overall_rating] || 'Logged'})`,
          subtitle: `${r.teacher_name} · Severity ${sev}/10${r.comment ? ' · ' + r.comment.substring(0, 60) + (r.comment.length > 60 ? '…' : '') : ''}`,
          details: r,
          color
        });
      });

      // Sort by date descending
      allEvents.sort((a, b) => new Date(b.date) - new Date(a.date));
      setEvents(allEvents);
    } catch (error) {
      console.error('Error fetching timeline data:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...events];

    // Apply type filters
    filtered = filtered.filter(e => {
      if (e.type.includes('medication') && !filters.medications) return false;
      if (e.type.includes('supplement') && !filters.supplements) return false;
      if (e.type === 'appointment' && !filters.appointments) return false;
      if (e.type === 'flare' && !filters.flares) return false;
      if (e.type === 'vaccine' && !filters.vaccines) return false;
      if (e.type === 'symptom' && !filters.symptoms) return false;
      if (e.type === 'sleep' && !filters.sleep) return false;
      if (e.type === 'weather' && !filters.weather) return false;
      if (e.type === 'cycle' && !filters.cycle) return false;
      if (e.type === 'lab' && !filters.labs) return false;
      if (e.type === 'teacher' && !filters.teacher) return false;
      return true;
    });

    // Apply time range filter
    const now = new Date();
    let cutoffDate = new Date();
    if (timeRange === '3m') cutoffDate.setMonth(now.getMonth() - 3);
    else if (timeRange === '6m') cutoffDate.setMonth(now.getMonth() - 6);
    else if (timeRange === '1y') cutoffDate.setFullYear(now.getFullYear() - 1);
    else cutoffDate = new Date('2000-01-01');

    filtered = filtered.filter(e => new Date(e.date) >= cutoffDate);

    setFilteredEvents(filtered);
  };

  const getEventIcon = (type) => {
    if (type.includes('medication')) return <Pill className="w-4 h-4" />;
    if (type.includes('supplement')) return <Pill className="w-4 h-4" />;
    if (type === 'appointment') return <Stethoscope className="w-4 h-4" />;
    if (type === 'flare') return <Flame className="w-4 h-4" />;
    if (type === 'vaccine') return <Syringe className="w-4 h-4" />;
    if (type === 'symptom') return <Activity className="w-4 h-4" />;
    if (type === 'sleep') return <Moon className="w-4 h-4" />;
    if (type === 'weather') return <Cloud className="w-4 h-4" />;
    if (type === 'cycle') return <Heart className="w-4 h-4" />;
    if (type === 'lab') return <TestTube className="w-4 h-4" />;
    if (type === 'teacher') return <GraduationCap className="w-4 h-4" />;
    return <Calendar className="w-4 h-4" />;
  };

  const getEventColor = (color) => {
    const colors = {
      purple: { bg: 'bg-purple-100', border: 'border-purple-400', text: 'text-purple-700', dot: 'bg-purple-500' },
      green: { bg: 'bg-green-100', border: 'border-green-400', text: 'text-green-700', dot: 'bg-green-500' },
      blue: { bg: 'bg-blue-100', border: 'border-blue-400', text: 'text-blue-700', dot: 'bg-blue-500' },
      orange: { bg: 'bg-orange-100', border: 'border-orange-400', text: 'text-orange-700', dot: 'bg-orange-500' },
      red: { bg: 'bg-red-100', border: 'border-red-400', text: 'text-red-700', dot: 'bg-red-500' },
      teal: { bg: 'bg-teal-100', border: 'border-teal-400', text: 'text-teal-700', dot: 'bg-teal-500' },
      yellow: { bg: 'bg-yellow-100', border: 'border-yellow-400', text: 'text-yellow-700', dot: 'bg-yellow-500' },
      indigo: { bg: 'bg-indigo-100', border: 'border-indigo-400', text: 'text-indigo-700', dot: 'bg-indigo-500' },
      sky: { bg: 'bg-sky-100', border: 'border-sky-400', text: 'text-sky-700', dot: 'bg-sky-500' },
      pink: { bg: 'bg-pink-100', border: 'border-pink-400', text: 'text-pink-700', dot: 'bg-pink-500' },
      cyan: { bg: 'bg-cyan-100', border: 'border-cyan-400', text: 'text-cyan-700', dot: 'bg-cyan-500' },
      emerald: { bg: 'bg-emerald-100', border: 'border-emerald-400', text: 'text-emerald-700', dot: 'bg-emerald-500' }
    };
    return colors[color] || colors.blue;
  };

  const formatDate = (dateStr) => {
    return formatLocalDate(dateStr, 'en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const groupEventsByMonth = () => {
    const groups = {};
    filteredEvents.forEach(event => {
      const monthYear = formatLocalDate(event.date, 'en-US', { month: 'long', year: 'numeric' });
      if (!groups[monthYear]) groups[monthYear] = [];
      groups[monthYear].push(event);
    });
    return groups;
  };

  // Generate graph data from flares and treatments
  const generateGraphData = () => {
    // Get time range cutoff
    const now = new Date();
    let cutoffDate = new Date();
    if (timeRange === '3m') cutoffDate.setMonth(now.getMonth() - 3);
    else if (timeRange === '6m') cutoffDate.setMonth(now.getMonth() - 6);
    else if (timeRange === '1y') cutoffDate.setFullYear(now.getFullYear() - 1);
    else cutoffDate = new Date('2000-01-01');

    // Filter flares by time range and sort by date
    const filteredFlares = rawFlares
      .filter(f => new Date(f.date) >= cutoffDate)
      .sort((a, b) => new Date(a.date) - new Date(b.date));

    // Create date-to-severity map
    const dataMap = {};
    filteredFlares.forEach(f => {
      const dateKey = f.date;
      dataMap[dateKey] = {
        date: dateKey,
        displayDate: formatLocalDate(f.date, 'en-US', { month: 'short', day: 'numeric' }),
        severity: f.severity,
        symptoms: f.symptoms?.length || 0
      };
    });

    // Get all medication/supplement events for reference lines
    const treatmentEvents = [];
    rawSupplements.forEach(s => {
      if (s.start_date && new Date(s.start_date) >= cutoffDate) {
        treatmentEvents.push({
          date: s.start_date,
          name: s.name,
          type: s.type === 'medication' ? 'med' : 'supp',
          event: 'start'
        });
      }
      if (s.end_date && new Date(s.end_date) >= cutoffDate) {
        treatmentEvents.push({
          date: s.end_date,
          name: s.name,
          type: s.type === 'medication' ? 'med' : 'supp',
          event: 'end'
        });
      }
    });

    // Add appointments (especially surgeries) as treatment events
    rawAppointments.forEach(a => {
      if (a.date && new Date(a.date) >= cutoffDate) {
        treatmentEvents.push({
          date: a.date,
          name: a.title || a.provider_name || 'Appointment',
          type: 'appointment',
          event: a.type === 'surgery' ? 'surgery' : 'visit'
        });
      }
    });

    return {
      chartData: Object.values(dataMap),
      treatmentEvents: treatmentEvents.sort((a, b) => new Date(a.date) - new Date(b.date))
    };
  };

  const { chartData, treatmentEvents } = generateGraphData();

  // Generate Health Metrics Comparison data
  const generateHealthMetricsData = () => {
    // Get time range cutoff
    const now = new Date();
    let cutoffDate = new Date();
    if (timeRange === '3m') cutoffDate.setMonth(now.getMonth() - 3);
    else if (timeRange === '6m') cutoffDate.setMonth(now.getMonth() - 6);
    else if (timeRange === '1y') cutoffDate.setFullYear(now.getFullYear() - 1);
    else cutoffDate = new Date('2000-01-01');

    // Create a map of dates to metrics
    const metricsMap = {};

    // Process flare data for overall severity and flare events
    rawFlares.filter(f => new Date(f.date) >= cutoffDate).forEach(f => {
      const dateKey = f.date;
      if (!metricsMap[dateKey]) {
        metricsMap[dateKey] = { date: dateKey, displayDate: formatLocalDate(f.date, 'en-US', { month: 'short', day: 'numeric' }) };
      }
      metricsMap[dateKey].flareSeverity = f.severity;
      metricsMap[dateKey].isFlare = f.severity >= 6 ? 10 : null; // Mark as flare if severity >= 6
    });

    // Process symptom data for OCD/anxiety and behavioral scores
    rawSymptoms.filter(s => new Date(s.date) >= cutoffDate).forEach(s => {
      const dateKey = s.date;
      if (!metricsMap[dateKey]) {
        metricsMap[dateKey] = { date: dateKey, displayDate: formatLocalDate(s.date, 'en-US', { month: 'short', day: 'numeric' }) };
      }
      metricsMap[dateKey].ocdAnxiety = s.ocd_anxiety_severity || s.severity;
      metricsMap[dateKey].behavioral = s.behavioral_severity || s.severity;
      metricsMap[dateKey].sleepQuality = s.sleep_quality;
      
      // Check for strep events
      if (s.strep_event && s.strep_event !== 'none') {
        metricsMap[dateKey].strepEvent = s.strep_event;
        metricsMap[dateKey].hasStrep = s.strep_event === 'positive_test' ? 10 : (s.strep_event === 'exposure' ? 5 : null);
      }
    });

    // Process sleep logs
    rawSleepLogs.filter(s => new Date(s.date) >= cutoffDate).forEach(s => {
      const dateKey = s.date;
      if (!metricsMap[dateKey]) {
        metricsMap[dateKey] = { date: dateKey, displayDate: formatLocalDate(s.date, 'en-US', { month: 'short', day: 'numeric' }) };
      }
      // Convert sleep quality - higher is better (invert if it's a "problems" scale)
      metricsMap[dateKey].sleepQuality = s.quality || s.sleep_quality || (10 - (s.disturbances || 0));
    });

    // Get strep events from flare journal as well (recent_illness field)
    rawFlares.filter(f => new Date(f.date) >= cutoffDate).forEach(f => {
      const dateKey = f.date;
      if (f.recent_illness && f.recent_illness.toLowerCase().includes('strep')) {
        if (!metricsMap[dateKey]) {
          metricsMap[dateKey] = { date: dateKey, displayDate: formatLocalDate(f.date, 'en-US', { month: 'short', day: 'numeric' }) };
        }
        metricsMap[dateKey].hasStrep = 10;
        metricsMap[dateKey].strepEvent = 'positive_test';
      }
    });

    // Sort by date
    const sortedData = Object.values(metricsMap).sort((a, b) => new Date(a.date) - new Date(b.date));
    
    return sortedData;
  };

  const healthMetricsData = generateHealthMetricsData();

  // Custom tooltip for health metrics
  const HealthMetricsTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-4 rounded-lg shadow-lg border border-stone-200 min-w-[200px]">
          <p className="font-semibold text-[#2D2D2D] border-b pb-2 mb-2">{data.displayDate}</p>
          {data.hasStrep && (
            <p className="text-red-600 font-medium flex items-center gap-2 mb-1">
              <Bug className="w-4 h-4" /> Strep {data.strepEvent === 'exposure' ? 'Exposure' : 'Infection'}
            </p>
          )}
          {data.flareSeverity && <p className="text-orange-600">Flare Severity: <span className="font-bold">{data.flareSeverity}/10</span></p>}
          {data.ocdAnxiety && <p className="text-purple-600">OCD/Anxiety: <span className="font-bold">{data.ocdAnxiety}/10</span></p>}
          {data.behavioral && <p className="text-blue-600">Behavioral: <span className="font-bold">{data.behavioral}/10</span></p>}
          {data.sleepQuality && <p className="text-indigo-600">Sleep Quality: <span className="font-bold">{data.sleepQuality}/10</span></p>}
        </div>
      );
    }
    return null;
  };

  // Custom tooltip for the graph
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-3 rounded-lg shadow-lg border border-stone-200">
          <p className="font-semibold text-[#2D2D2D]">{data.displayDate}</p>
          <p className="text-orange-600">Severity: <span className="font-bold">{data.severity}/10</span></p>
          <p className="text-[#57534E] text-sm">{data.symptoms} symptoms logged</p>
        </div>
      );
    }
    return null;
  };

  const groupedEvents = groupEventsByMonth();

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 to-blue-50 pb-20">
      {/* Header */}
      <div className="bg-white border-b border-stone-100 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="p-2">
                <ChevronLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-xl font-nunito font-bold text-[#2D2D2D]">Medical History Timeline</h1>
                <p className="text-sm text-[#57534E]">Visualize medications, appointments, and flares over time</p>
              </div>
            </div>
            {/* View Toggle */}
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={() => setViewMode('timeline')}
                className={`px-4 py-2 rounded-full text-sm font-medium flex items-center gap-1.5 transition-all border ${
                  viewMode === 'timeline' 
                    ? 'bg-blue-500 text-white border-blue-500 shadow-md' 
                    : 'bg-white text-blue-600 border-blue-200 hover:bg-blue-50'
                }`}
                data-testid="timeline-view-btn"
              >
                <List className="w-4 h-4" /> List
              </button>
              <button
                onClick={() => setViewMode('graph')}
                className={`px-4 py-2 rounded-full text-sm font-medium flex items-center gap-1.5 transition-all border ${
                  viewMode === 'graph' 
                    ? 'bg-purple-500 text-white border-purple-500 shadow-md' 
                    : 'bg-white text-purple-600 border-purple-200 hover:bg-purple-50'
                }`}
                data-testid="graph-view-btn"
              >
                <LineChart className="w-4 h-4" /> Graph
              </button>
              <button
                onClick={() => setViewMode('metrics')}
                className={`px-4 py-2 rounded-full text-sm font-medium flex items-center gap-1.5 transition-all border ${
                  viewMode === 'metrics' 
                    ? 'bg-green-500 text-white border-green-500 shadow-md' 
                    : 'bg-white text-green-600 border-green-200 hover:bg-green-50'
                }`}
                data-testid="metrics-view-btn"
              >
                <Activity className="w-4 h-4" /> Metrics
              </button>
              <button
                onClick={() => setViewMode('insights')}
                className={`px-4 py-2 rounded-full text-sm font-medium flex items-center gap-1.5 transition-all border ${
                  viewMode === 'insights' 
                    ? 'bg-orange-500 text-white border-orange-500 shadow-md' 
                    : 'bg-white text-orange-600 border-orange-200 hover:bg-orange-50'
                }`}
                data-testid="insights-view-btn"
              >
                <TrendingUp className="w-4 h-4" /> Insights
              </button>
              <button
                onClick={() => setViewMode('correlations')}
                className={`px-4 py-2 rounded-full text-sm font-medium flex items-center gap-1.5 transition-all border ${
                  viewMode === 'correlations' 
                    ? 'bg-amber-500 text-white border-amber-500 shadow-md' 
                    : 'bg-white text-amber-600 border-amber-200 hover:bg-amber-50'
                }`}
                data-testid="correlations-view-btn"
              >
                <Lightbulb className="w-4 h-4" /> Correlations
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Filters */}
        <Card className="bg-white rounded-xl border p-4">
          <div className="flex flex-wrap gap-4 items-center justify-between">
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setFilters({ ...filters, medications: !filters.medications })}
                className={`px-3 py-1.5 rounded-full text-sm font-medium flex items-center gap-1.5 transition-all ${
                  filters.medications ? 'bg-purple-100 text-purple-700 border-2 border-purple-300' : 'bg-stone-100 text-stone-500'
                }`}
              >
                <Pill className="w-3.5 h-3.5" /> Medications
              </button>
              <button
                onClick={() => setFilters({ ...filters, supplements: !filters.supplements })}
                className={`px-3 py-1.5 rounded-full text-sm font-medium flex items-center gap-1.5 transition-all ${
                  filters.supplements ? 'bg-green-100 text-green-700 border-2 border-green-300' : 'bg-stone-100 text-stone-500'
                }`}
              >
                <Pill className="w-3.5 h-3.5" /> Supplements
              </button>
              <button
                onClick={() => setFilters({ ...filters, appointments: !filters.appointments })}
                className={`px-3 py-1.5 rounded-full text-sm font-medium flex items-center gap-1.5 transition-all ${
                  filters.appointments ? 'bg-blue-100 text-blue-700 border-2 border-blue-300' : 'bg-stone-100 text-stone-500'
                }`}
              >
                <Stethoscope className="w-3.5 h-3.5" /> Appointments
              </button>
              <button
                onClick={() => setFilters({ ...filters, flares: !filters.flares })}
                className={`px-3 py-1.5 rounded-full text-sm font-medium flex items-center gap-1.5 transition-all ${
                  filters.flares ? 'bg-orange-100 text-orange-700 border-2 border-orange-300' : 'bg-stone-100 text-stone-500'
                }`}
              >
                <Flame className="w-3.5 h-3.5" /> Flares
              </button>
              <button
                onClick={() => setFilters({ ...filters, vaccines: !filters.vaccines })}
                className={`px-3 py-1.5 rounded-full text-sm font-medium flex items-center gap-1.5 transition-all ${
                  filters.vaccines ? 'bg-teal-100 text-teal-700 border-2 border-teal-300' : 'bg-stone-100 text-stone-500'
                }`}
              >
                <Syringe className="w-3.5 h-3.5" /> Vaccines
              </button>
              <button
                onClick={() => setFilters({ ...filters, symptoms: !filters.symptoms })}
                className={`px-3 py-1.5 rounded-full text-sm font-medium flex items-center gap-1.5 transition-all ${
                  filters.symptoms ? 'bg-yellow-100 text-yellow-700 border-2 border-yellow-300' : 'bg-stone-100 text-stone-500'
                }`}
              >
                <Activity className="w-3.5 h-3.5" /> Symptoms
              </button>
              <button
                onClick={() => setFilters({ ...filters, sleep: !filters.sleep })}
                className={`px-3 py-1.5 rounded-full text-sm font-medium flex items-center gap-1.5 transition-all ${
                  filters.sleep ? 'bg-indigo-100 text-indigo-700 border-2 border-indigo-300' : 'bg-stone-100 text-stone-500'
                }`}
              >
                <Moon className="w-3.5 h-3.5" /> Sleep
              </button>
              <button
                onClick={() => setFilters({ ...filters, labs: !filters.labs })}
                className={`px-3 py-1.5 rounded-full text-sm font-medium flex items-center gap-1.5 transition-all ${
                  filters.labs ? 'bg-cyan-100 text-cyan-700 border-2 border-cyan-300' : 'bg-stone-100 text-stone-500'
                }`}
              >
                <TestTube className="w-3.5 h-3.5" /> Labs
              </button>
              <button
                onClick={() => setFilters({ ...filters, teacher: !filters.teacher })}
                className={`px-3 py-1.5 rounded-full text-sm font-medium flex items-center gap-1.5 transition-all ${
                  filters.teacher ? 'bg-emerald-100 text-emerald-700 border-2 border-emerald-300' : 'bg-stone-100 text-stone-500'
                }`}
                data-testid="filter-teacher"
              >
                <GraduationCap className="w-3.5 h-3.5" /> Teacher
              </button>
            </div>
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              className="h-9 px-3 bg-white border border-stone-200 rounded-lg text-sm"
            >
              <option value="3m">Last 3 months</option>
              <option value="6m">Last 6 months</option>
              <option value="1y">Last year</option>
              <option value="all">All time</option>
            </select>
          </div>
        </Card>

        {/* Stats Summary */}
        <div className="grid grid-cols-5 gap-3">
          <Card className="bg-purple-50 border-purple-200 rounded-xl p-3 text-center">
            <p className="text-2xl font-bold text-purple-700">{filteredEvents.filter(e => e.type.includes('medication')).length}</p>
            <p className="text-xs text-purple-600">Med Events</p>
          </Card>
          <Card className="bg-green-50 border-green-200 rounded-xl p-3 text-center">
            <p className="text-2xl font-bold text-green-700">{filteredEvents.filter(e => e.type.includes('supplement')).length}</p>
            <p className="text-xs text-green-600">Supp Events</p>
          </Card>
          <Card className="bg-blue-50 border-blue-200 rounded-xl p-3 text-center">
            <p className="text-2xl font-bold text-blue-700">{filteredEvents.filter(e => e.type === 'appointment').length}</p>
            <p className="text-xs text-blue-600">Appointments</p>
          </Card>
          <Card className="bg-orange-50 border-orange-200 rounded-xl p-3 text-center">
            <p className="text-2xl font-bold text-orange-700">{filteredEvents.filter(e => e.type === 'flare').length}</p>
            <p className="text-xs text-orange-600">Flare Days</p>
          </Card>
          <Card className="bg-teal-50 border-teal-200 rounded-xl p-3 text-center">
            <p className="text-2xl font-bold text-teal-700">{filteredEvents.filter(e => e.type === 'vaccine').length}</p>
            <p className="text-xs text-teal-600">Vaccines</p>
          </Card>
        </div>

        {/* Graph View */}
        {viewMode === 'graph' && (
          <Card className="bg-white rounded-xl border p-6" data-testid="timeline-graph">
            <div className="mb-4">
              <h2 className="text-lg font-nunito font-bold text-[#2D2D2D] flex items-center gap-2">
                <TrendingDown className="w-5 h-5 text-primary" />
                Symptom Severity Over Time
              </h2>
              <p className="text-sm text-[#57534E]">Track flare severity with treatment events marked</p>
            </div>
            
            {chartData.length === 0 ? (
              <div className="h-64 flex items-center justify-center text-[#A8A29E]">
                <div className="text-center">
                  <Flame className="w-12 h-12 mx-auto mb-3 text-stone-300" />
                  <p>No flare data to display</p>
                  <p className="text-sm">Start logging flares to see the graph</p>
                </div>
              </div>
            ) : (
              <>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={chartData} margin={{ top: 20, right: 30, left: 10, bottom: 20 }}>
                      <defs>
                        <linearGradient id="severityGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#f97316" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#f97316" stopOpacity={0.05}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
                      <XAxis 
                        dataKey="displayDate" 
                        tick={{ fontSize: 12, fill: '#78716c' }}
                        axisLine={{ stroke: '#d6d3d1' }}
                      />
                      <YAxis 
                        domain={[0, 10]} 
                        ticks={[0, 2, 4, 6, 8, 10]}
                        tick={{ fontSize: 12, fill: '#78716c' }}
                        axisLine={{ stroke: '#d6d3d1' }}
                        label={{ value: 'Severity', angle: -90, position: 'insideLeft', style: { fontSize: 12, fill: '#78716c' } }}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Area 
                        type="monotone" 
                        dataKey="severity" 
                        fill="url(#severityGradient)" 
                        stroke="transparent"
                      />
                      <Line 
                        type="monotone" 
                        dataKey="severity" 
                        stroke="#f97316" 
                        strokeWidth={3}
                        dot={{ fill: '#f97316', strokeWidth: 2, r: 5 }}
                        activeDot={{ r: 8, stroke: '#f97316', strokeWidth: 2 }}
                      />
                      
                      {/* Treatment event reference lines */}
                      {treatmentEvents.map((event, i) => {
                        // Determine color based on event type
                        let strokeColor = '#22c55e'; // green for supplements
                        if (event.type === 'med') strokeColor = '#8b5cf6'; // purple for medications
                        if (event.type === 'appointment') strokeColor = '#3b82f6'; // blue for appointments/surgery
                        
                        // Determine symbol
                        let symbol = '▶'; // start
                        if (event.event === 'end') symbol = '■';
                        if (event.event === 'surgery') symbol = '⚕'; // medical cross for surgery
                        if (event.event === 'visit') symbol = '●';
                        
                        return (
                          <ReferenceLine
                            key={i}
                            x={formatLocalDate(event.date, 'en-US', { month: 'short', day: 'numeric' })}
                            stroke={strokeColor}
                            strokeDasharray={event.event === 'end' ? '5 5' : '0'}
                            strokeWidth={event.event === 'surgery' ? 3 : 2}
                            label={{
                              value: symbol,
                              position: 'top',
                              fill: strokeColor,
                              fontSize: event.event === 'surgery' ? 14 : 10
                            }}
                          />
                        );
                      })}
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
                
                {/* Treatment Events Legend */}
                {treatmentEvents.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-stone-100">
                    <p className="text-sm font-medium text-[#57534E] mb-3">Treatment Events on Graph:</p>
                    <div className="flex flex-wrap gap-3">
                      {treatmentEvents.map((event, i) => {
                        // Determine styling based on type
                        let bgClass = 'bg-green-100 text-green-700 border-green-200';
                        if (event.type === 'med') bgClass = 'bg-purple-100 text-purple-700 border-purple-200';
                        if (event.type === 'appointment') bgClass = 'bg-blue-100 text-blue-700 border-blue-200';
                        
                        // Determine symbol
                        let symbol = '▶';
                        if (event.event === 'end') symbol = '■';
                        if (event.event === 'surgery') symbol = '⚕';
                        if (event.event === 'visit') symbol = '●';
                        
                        return (
                          <div 
                            key={i}
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-2 border ${bgClass}`}
                          >
                            <span>{symbol}</span>
                            <span>{event.name}</span>
                            <span className="text-xs opacity-70">
                              ({formatLocalDate(event.date, 'en-US', { month: 'short', day: 'numeric' })})
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            )}
          </Card>
        )}

        {/* Health Metrics Comparison View */}
        {viewMode === 'metrics' && (
          <Card className="bg-white rounded-xl border p-6" data-testid="health-metrics-graph">
            <div className="mb-4">
              <h2 className="text-lg font-nunito font-bold text-[#2D2D2D] flex items-center gap-2">
                <Activity className="w-5 h-5 text-primary" />
                Health Metrics Comparison
              </h2>
              <p className="text-sm text-[#57534E]">Track strep infections, symptoms, and flares to identify correlations</p>
            </div>
            
            {healthMetricsData.length === 0 ? (
              <div className="h-64 flex items-center justify-center text-[#A8A29E]">
                <div className="text-center">
                  <Activity className="w-12 h-12 mx-auto mb-3 text-stone-300" />
                  <p>No health metrics data to display</p>
                  <p className="text-sm">Log symptoms and flares to see the comparison chart</p>
                </div>
              </div>
            ) : (
              <>
                {/* Legend */}
                <div className="flex flex-wrap gap-4 mb-4 p-3 bg-stone-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-red-500"></div>
                    <span className="text-xs font-medium text-stone-600">Strep Infection</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-red-400 ring-2 ring-red-200"></div>
                    <span className="text-xs font-medium text-stone-600">Strep Exposure</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-orange-500"></div>
                    <span className="text-xs font-medium text-stone-600">Flare Severity</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-purple-500"></div>
                    <span className="text-xs font-medium text-stone-600">OCD/Anxiety</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                    <span className="text-xs font-medium text-stone-600">Behavioral</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                    <span className="text-xs font-medium text-stone-600">Sleep Quality</span>
                  </div>
                </div>

                <div className="h-96">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={healthMetricsData} margin={{ top: 20, right: 30, left: 10, bottom: 20 }}>
                      <defs>
                        <linearGradient id="strepGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#ef4444" stopOpacity={0.2}/>
                          <stop offset="95%" stopColor="#ef4444" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
                      <XAxis 
                        dataKey="displayDate" 
                        tick={{ fontSize: 11, fill: '#78716c' }}
                        axisLine={{ stroke: '#d6d3d1' }}
                        interval="preserveStartEnd"
                      />
                      <YAxis 
                        domain={[0, 10]} 
                        ticks={[0, 2, 4, 6, 8, 10]}
                        tick={{ fontSize: 12, fill: '#78716c' }}
                        axisLine={{ stroke: '#d6d3d1' }}
                        label={{ value: 'Severity / Quality', angle: -90, position: 'insideLeft', style: { fontSize: 11, fill: '#78716c' } }}
                      />
                      <Tooltip content={<HealthMetricsTooltip />} />
                      <Legend 
                        formatter={(value) => {
                          // Hide "Strep Event" from legend since it's explained below
                          if (value === 'Strep Event' || value === 'Strep Marker') return null;
                          return value;
                        }}
                      />
                      
                      {/* Strep events as area background - hidden from legend */}
                      <Area 
                        type="step" 
                        dataKey="hasStrep" 
                        fill="url(#strepGradient)" 
                        stroke="#ef4444"
                        strokeWidth={0}
                        name="Strep Event"
                        dot={false}
                        legendType="none"
                      />
                      
                      {/* Flare severity line */}
                      <Line 
                        type="monotone" 
                        dataKey="flareSeverity" 
                        stroke="#f97316" 
                        strokeWidth={3}
                        dot={{ fill: '#f97316', strokeWidth: 2, r: 4 }}
                        name="Flare Severity"
                        connectNulls
                      />
                      
                      {/* OCD/Anxiety line */}
                      <Line 
                        type="monotone" 
                        dataKey="ocdAnxiety" 
                        stroke="#a855f7" 
                        strokeWidth={2}
                        dot={{ fill: '#a855f7', strokeWidth: 2, r: 3 }}
                        name="OCD/Anxiety"
                        connectNulls
                      />
                      
                      {/* Behavioral severity line */}
                      <Line 
                        type="monotone" 
                        dataKey="behavioral" 
                        stroke="#3b82f6" 
                        strokeWidth={2}
                        dot={{ fill: '#3b82f6', strokeWidth: 2, r: 3 }}
                        name="Behavioral"
                        connectNulls
                      />
                      
                      {/* Sleep quality line (inverted - higher is better) */}
                      <Line 
                        type="monotone" 
                        dataKey="sleepQuality" 
                        stroke="#22c55e" 
                        strokeWidth={2}
                        strokeDasharray="5 5"
                        dot={{ fill: '#22c55e', strokeWidth: 2, r: 3 }}
                        name="Sleep Quality"
                        connectNulls
                      />

                      {/* Strep event markers - hidden from legend */}
                      <Scatter 
                        dataKey="hasStrep" 
                        fill="#ef4444" 
                        name="Strep Marker"
                        legendType="none"
                        shape={(props) => {
                          if (!props.payload.hasStrep) return null;
                          return (
                            <circle 
                              cx={props.cx} 
                              cy={props.cy - 30} 
                              r={8} 
                              fill="#ef4444" 
                              stroke="#fff"
                              strokeWidth={2}
                            />
                          );
                        }}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>

                {/* Correlation Insight Box */}
                <div className="mt-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <div className="flex items-start gap-3">
                    <Bug className="w-5 h-5 text-amber-600 mt-0.5" />
                    <div>
                      <p className="font-semibold text-amber-800 mb-1">Understanding the Chart</p>
                      <p className="text-sm text-amber-700">
                        <strong>Red shaded areas</strong> indicate strep exposure or infection events. 
                        Look for symptom spikes (orange, purple, blue lines) that follow strep events - 
                        this is the hallmark pattern of PANDAS/PANS. Sleep quality (dashed line) often 
                        drops during flares. Higher values = worse symptoms (except sleep, where higher = better).
                      </p>
                    </div>
                  </div>
                </div>
              </>
            )}
          </Card>
        )}

        {/* Insights View */}
        {viewMode === 'insights' && (
          <div className="-mx-4 -mt-6">
            <InsightsPage embedded={true} />
          </div>
        )}

        {/* Correlation Insights View */}
        {viewMode === 'correlations' && (
          <div className="space-y-8">
            {/* Correlation Insights Section */}
            <CorrelationInsights embedded={true} />
            
            {/* Divider */}
            <div className="border-t border-stone-200 my-8"></div>
            
            {/* Smart Correlations Section */}
            <SmartCorrelations embedded={true} />
            
            {/* Divider */}
            <div className="border-t border-stone-200 my-8"></div>
            
            {/* Food Correlations Section */}
            <CorrelationAnalyzer embedded={true} />
          </div>
        )}

        {/* Timeline List View */}
        {viewMode === 'timeline' && (loading ? (
          <Card className="bg-white rounded-xl p-8 text-center">
            <p className="text-[#57534E]">Loading timeline...</p>
          </Card>
        ) : filteredEvents.length === 0 ? (
          <Card className="bg-white rounded-xl p-8 text-center">
            <Calendar className="w-12 h-12 text-stone-300 mx-auto mb-4" />
            <h3 className="font-semibold mb-2">No events to display</h3>
            <p className="text-sm text-[#57534E]">Start tracking medications, appointments, and flares to see your timeline.</p>
          </Card>
        ) : (
          <div className="space-y-8">
            {Object.entries(groupedEvents).map(([monthYear, monthEvents]) => (
              <div key={monthYear}>
                {/* Month Header */}
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-accent rounded-full flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-white" />
                  </div>
                  <h2 className="text-lg font-nunito font-bold text-[#2D2D2D]">{monthYear}</h2>
                  <div className="flex-1 h-px bg-stone-200"></div>
                  <span className="text-sm text-[#A8A29E]">{monthEvents.length} events</span>
                </div>

                {/* Events for this month */}
                <div className="relative ml-5 pl-6 border-l-2 border-stone-200 space-y-4">
                  {monthEvents.map((event) => {
                    const colorClasses = getEventColor(event.color);
                    return (
                      <div
                        key={event.id}
                        className={`relative cursor-pointer transition-all hover:scale-[1.01] ${
                          selectedEvent?.id === event.id ? 'scale-[1.01]' : ''
                        }`}
                        onClick={() => setSelectedEvent(selectedEvent?.id === event.id ? null : event)}
                      >
                        {/* Timeline dot */}
                        <div className={`absolute -left-[31px] w-4 h-4 rounded-full ${colorClasses.dot} border-2 border-white shadow`}></div>
                        
                        {/* Event card */}
                        <Card className={`${colorClasses.bg} ${colorClasses.border} border-l-4 rounded-lg p-3`}>
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-3">
                              <div className={`p-1.5 rounded-lg bg-white/50 ${colorClasses.text}`}>
                                {getEventIcon(event.type)}
                              </div>
                              <div>
                                <p className={`font-semibold ${colorClasses.text}`}>{event.title}</p>
                                <p className="text-sm text-stone-600">{event.subtitle}</p>
                              </div>
                            </div>
                            <span className="text-xs text-stone-500 whitespace-nowrap flex items-center gap-1.5">
                              {event.attachmentCount > 0 && (
                                <span
                                  className="flex items-center gap-0.5 text-blue-600 bg-white/70 rounded-full px-1.5 py-0.5"
                                  title={`${event.attachmentCount} attachment${event.attachmentCount > 1 ? 's' : ''}`}
                                  data-testid={`event-attachment-badge-${event.id}`}
                                >
                                  <Paperclip className="w-3 h-3" />
                                  <span className="font-semibold">{event.attachmentCount}</span>
                                </span>
                              )}
                              {formatDate(event.date)}
                            </span>
                          </div>

                          {/* Expanded details */}
                          {selectedEvent?.id === event.id && (
                            <div className="mt-3 pt-3 border-t border-stone-200/50 text-sm text-stone-700 space-y-2">
                              {event.type === 'appointment' && event.details.summary && (
                                <p><strong>Summary:</strong> {event.details.summary}</p>
                              )}
                              {event.type === 'appointment' && event.details.meds_ordered?.length > 0 && (
                                <p><strong>Meds Ordered:</strong> {event.details.meds_ordered.join(', ')}</p>
                              )}
                              {event.type === 'flare' && event.details.symptoms?.length > 0 && (
                                <p><strong>Symptoms:</strong> {event.details.symptoms.join(', ')}</p>
                              )}
                              {event.type === 'flare' && event.details.what_helped && (
                                <p><strong>What Helped:</strong> {event.details.what_helped}</p>
                              )}
                              {(event.type.includes('medication') || event.type.includes('supplement')) && event.details.reason && (
                                <p><strong>Reason:</strong> {event.details.reason}</p>
                              )}
                              {(event.type.includes('medication') || event.type.includes('supplement')) && event.details.brand && (
                                <p><strong>Brand:</strong> {event.details.brand}</p>
                              )}
                              {event.details.notes && (
                                <p><strong>Notes:</strong> {event.details.notes}</p>
                              )}
                            </div>
                          )}
                        </Card>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

export default MedicalTimeline;
